import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardText, BellRinging } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import { fmtDate } from "@/lib/format";
import RegistrationDetailDrawer from "@/components/RegistrationDetailDrawer";
import BranchFilter from "@/components/BranchFilter";

const BucketTile = ({ b, active, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`ps-bucket-${b.key}`}
    className={`text-left border-2 p-4 transition-colors ${active ? "border-ink bg-canvas-card" : "border-canvas-line hover:border-ink"}`}
  >
    <div className="flex items-center gap-2 mb-1">
      {b.is_reminder ? <BellRinging size={14} weight="bold" /> : <ClipboardText size={14} weight="bold" />}
      <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-muted leading-tight">{b.label}</div>
    </div>
    <div className="font-display text-3xl font-bold tabular-nums" data-testid={`ps-bucket-count-${b.key}`}>{b.count}</div>
    <div className="font-mono text-[10px] text-ink-muted">{b.is_reminder ? "due now" : "pending"}</div>
  </button>
);

const BucketTable = ({ rows, onOpen }) => (
  <table className="w-full text-sm" data-testid="ps-bucket-table">
    <thead className="bg-canvas-panel">
      <tr>
        {["Customer", "Vehicle", "Branch", "Started", "Due", "Overdue"].map((h) => (
          <th key={h} className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">{h}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id} onClick={() => onOpen(r.id)} data-testid={`ps-row-${r.id}`} className="border-t border-canvas-line cursor-pointer hover:bg-canvas-panel">
          <td className="px-3 py-2">
            <div className="font-semibold">{r.customer_name || "—"}</div>
            <div className="font-mono text-[10px] text-ink-muted">{r.customer_phone || ""}</div>
          </td>
          <td className="px-3 py-2">
            <div className="font-mono text-xs">{r.vehicle_model || "—"}</div>
            <div className="font-mono text-[10px] text-ink-muted">{r.vehicle_vin}</div>
          </td>
          <td className="px-3 py-2 font-mono text-[11px] text-ink-muted">{r.branch_name || "—"}</td>
          <td className="px-3 py-2 font-mono text-xs text-ink-muted">{fmtDate(r.started_at)}</td>
          <td className="px-3 py-2 font-mono text-xs">{fmtDate(r.due_date)}</td>
          <td className="px-3 py-2">
            <span className={`inline-block px-1.5 py-0.5 border text-[10px] font-mono uppercase ${r.days_overdue > 7 ? "border-destructive text-destructive" : r.days_overdue > 0 ? "border-[#B67100] text-[#B67100]" : "border-canvas-line"}`}>
              {r.days_overdue}d
            </span>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default function PostSaleTasksPage() {
  const [buckets, setBuckets] = useState([]);
  const [active, setActive] = useState("dms");
  const [rows, setRows] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [includeImported, setIncludeImported] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (branchFilter) params.branch_id = branchFilter;
    if (search) params.q = search;
    if (includeImported) params.include_imported = true;
    try {
      const [{ data: b }, { data: r }] = await Promise.all([
        api.get("/registrations/post-sale/buckets", { params }),
        api.get(`/registrations/post-sale/buckets/${active}`, { params }),
      ]);
      setBuckets(b || []);
      setRows(r || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [active, branchFilter, search, includeImported]);

  useEffect(() => { load(); }, [load]);

  const activeLabel = buckets.find((b) => b.key === active)?.label || "";

  return (
    <div className="px-6 py-6" data-testid="post-sale-page">
      <PageHeader
        title="Post-Sale Tasks"
        subtitle="One bucket per task. Reminders appear only once they are due."
        eyebrow="Operations"
        actions={<BranchFilter value={branchFilter} onChange={setBranchFilter} testId="ps-branch-filter" kind="sales" />}
      />
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="ps-buckets">
        {buckets.map((b) => <BucketTile key={b.key} b={b} active={b.key === active} onClick={() => setActive(b.key)} />)}
      </div>
      <div className="flex items-center gap-3 flex-wrap mb-4 px-1">
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="VIN / Chassis / Mobile / Customer name"
          data-testid="ps-search-input"
          className="flex-1 min-w-[220px] px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:border-ink focus:outline-none"
        />
        <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-ink-muted cursor-pointer">
          <input type="checkbox" checked={includeImported} onChange={(e) => setIncludeImported(e.target.checked)} data-testid="ps-include-imported" className="w-3.5 h-3.5" />
          Show migrated
        </label>
      </div>
      <div className="border-2 border-ink">
        <div className="px-4 py-3 border-b border-canvas-line flex items-center gap-3">
          <h2 className="font-display font-semibold" data-testid="ps-active-title">{activeLabel}</h2>
          <span className="font-mono text-[11px] text-ink-muted ml-auto">{loading ? "Loading…" : `${rows.length} record${rows.length === 1 ? "" : "s"}`}</span>
        </div>
        {rows.length === 0 ? (
          <div className="p-10 text-center text-ink-muted text-sm" data-testid="ps-empty">Nothing pending in this bucket.</div>
        ) : (
          <div className="overflow-x-auto"><BucketTable rows={rows} onOpen={setSelectedId} /></div>
        )}
      </div>
      <RegistrationDetailDrawer registrationId={selectedId} onlyStageKey={active} onClose={() => setSelectedId(null)} onChanged={load} />
    </div>
  );
}
